import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import rsocService, {
  KeywordStats,
  CategoryBreakdown,
  CampaignBreakdown,
  AdBreakdown,
} from '@/services/rsoc.service';

/**
 * Query Keys for RSOC data caching
 */
export const rsocKeys = {
  all: ['rsoc'] as const,
  keywordStats: (startDate: string, endDate: string, search?: string) =>
    ['rsoc', 'keyword-stats', startDate, endDate, search] as const,
  categoryBreakdown: (startDate: string, endDate: string) =>
    ['rsoc', 'category-breakdown', startDate, endDate] as const,
  categoryKeywords: (category: string, startDate: string, endDate: string) =>
    ['rsoc', 'category-keywords', category, startDate, endDate] as const,
  categoryKeywordDaily: (category: string, keyword: string, startDate: string, endDate: string) =>
    ['rsoc', 'category-keyword-daily', category, keyword, startDate, endDate] as const,
  campaigns: (startDate: string, endDate: string) =>
    ['rsoc', 'campaigns', startDate, endDate] as const,
  campaignKeywords: (campaignId: string, startDate: string, endDate: string) =>
    ['rsoc', 'campaign-keywords', campaignId, startDate, endDate] as const,
  campaignKeywordDaily: (campaignId: string, keyword: string, startDate: string, endDate: string) =>
    ['rsoc', 'campaign-keyword-daily', campaignId, keyword, startDate, endDate] as const,
  ads: (startDate: string, endDate: string) =>
    ['rsoc', 'ads', startDate, endDate] as const,
  adKeywords: (adId: string, startDate: string, endDate: string) =>
    ['rsoc', 'ad-keywords', adId, startDate, endDate] as const,
  adKeywordDaily: (adId: string, keyword: string, startDate: string, endDate: string) =>
    ['rsoc', 'ad-keyword-daily', adId, keyword, startDate, endDate] as const,
};

/**
 * Hook: useKeywordStats
 * Fetches keyword-level statistics with daily breakdown
 */
export const useKeywordStats = (
  startDate: string,
  endDate: string,
  search?: string,
  options?: Omit<UseQueryOptions<KeywordStats[]>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: rsocKeys.keywordStats(startDate, endDate, search),
    queryFn: () => rsocService.getKeywordStats(startDate, endDate, search),
    enabled: Boolean(startDate && endDate) && (options?.enabled !== false),
    ...options,
  });
};

/**
 * Hook: useCategoryBreakdown
 * Fetches category-level breakdown
 */
export const useCategoryBreakdown = (
  startDate: string,
  endDate: string,
  options?: Omit<UseQueryOptions<CategoryBreakdown[]>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: rsocKeys.categoryBreakdown(startDate, endDate),
    queryFn: () => rsocService.getCategoryBreakdown(startDate, endDate),
    enabled: Boolean(startDate && endDate) && (options?.enabled !== false),
    ...options,
  });
};

/**
 * Hook: useCategoryKeywords
 * Fetches keywords for a specific category
 */
export const useCategoryKeywords = (
  category: string,
  startDate: string,
  endDate: string,
  options?: Omit<UseQueryOptions<any[]>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: rsocKeys.categoryKeywords(category, startDate, endDate),
    queryFn: async () => {
      const keywords = await rsocService.getCategoryKeywords(category, startDate, endDate);
      return keywords.map((kw: any) => ({
        ...kw,
        total_revenue: parseFloat(kw.total_revenue || kw.revenue || 0),
        total_clicks: parseInt(kw.total_clicks || kw.clicks || 0),
        avg_rpc: parseFloat(kw.avg_rpc || kw.rpc || 0),
        days_active: parseInt(kw.days_active || 0),
      }));
    },
    enabled: Boolean(category && startDate && endDate) && (options?.enabled !== false),
    ...options,
  });
};

/**
 * Hook: useCategoryKeywordDaily
 * Fetches daily breakdown for a keyword in a category
 */
export const useCategoryKeywordDaily = (
  category: string,
  keyword: string,
  startDate: string,
  endDate: string,
  options?: Omit<UseQueryOptions<any[]>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: rsocKeys.categoryKeywordDaily(category, keyword, startDate, endDate),
    queryFn: async () => {
      const daily = await rsocService.getCategoryKeywordDaily(category, keyword, startDate, endDate);
      return daily.map((d: any) => ({
        date: d.date,
        rpc: parseFloat(d.rpc || 0),
        revenue: parseFloat(d.revenue || 0),
        clicks: parseInt(d.clicks || 0),
      }));
    },
    enabled: Boolean(category && keyword && startDate && endDate) && (options?.enabled !== false),
    ...options,
  });
};

/**
 * Hook: useCampaigns
 * Fetches campaign-level breakdown
 */
export const useCampaigns = (
  startDate: string,
  endDate: string,
  options?: Omit<UseQueryOptions<CampaignBreakdown[]>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: rsocKeys.campaigns(startDate, endDate),
    queryFn: () => rsocService.getCampaigns(startDate, endDate),
    enabled: Boolean(startDate && endDate) && (options?.enabled !== false),
    ...options,
  });
};

/**
 * Hook: useCampaignKeywords
 * Fetches keywords for a specific campaign
 */
export const useCampaignKeywords = (
  campaignId: string,
  startDate: string,
  endDate: string,
  options?: Omit<UseQueryOptions<any[]>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: rsocKeys.campaignKeywords(campaignId, startDate, endDate),
    queryFn: async () => {
      const keywords = await rsocService.getCampaignKeywords(campaignId, startDate, endDate);
      return keywords.map((kw: any) => ({
        ...kw,
        total_revenue: parseFloat(kw.total_revenue || kw.revenue || 0),
        total_clicks: parseInt(kw.total_clicks || kw.clicks || 0),
        avg_rpc: parseFloat(kw.avg_rpc || kw.rpc || 0),
        days_active: parseInt(kw.days_active || 0),
      }));
    },
    enabled: Boolean(campaignId && startDate && endDate) && (options?.enabled !== false),
    ...options,
  });
};

/**
 * Hook: useCampaignKeywordDaily
 * Fetches daily breakdown for a keyword in a campaign
 */
export const useCampaignKeywordDaily = (
  campaignId: string,
  keyword: string,
  startDate: string,
  endDate: string,
  options?: Omit<UseQueryOptions<any[]>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: rsocKeys.campaignKeywordDaily(campaignId, keyword, startDate, endDate),
    queryFn: async () => {
      const daily = await rsocService.getCampaignKeywordDaily(campaignId, keyword, startDate, endDate);
      return daily.map((d: any) => ({
        date: d.date,
        rpc: parseFloat(d.rpc || 0),
        revenue: parseFloat(d.revenue || 0),
        clicks: parseInt(d.clicks || 0),
      }));
    },
    enabled: Boolean(campaignId && keyword && startDate && endDate) && (options?.enabled !== false),
    ...options,
  });
};

/**
 * Hook: useAds
 * Fetches ad-level breakdown
 */
export const useAds = (
  startDate: string,
  endDate: string,
  options?: Omit<UseQueryOptions<AdBreakdown[]>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: rsocKeys.ads(startDate, endDate),
    queryFn: () => rsocService.getAds(startDate, endDate),
    enabled: Boolean(startDate && endDate) && (options?.enabled !== false),
    ...options,
  });
};

/**
 * Hook: useAdKeywords
 * Fetches keywords for a specific ad
 */
export const useAdKeywords = (
  adId: string,
  startDate: string,
  endDate: string,
  options?: Omit<UseQueryOptions<any[]>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: rsocKeys.adKeywords(adId, startDate, endDate),
    queryFn: async () => {
      const keywords = await rsocService.getAdKeywords(adId, startDate, endDate);
      return keywords.map((kw: any) => ({
        ...kw,
        total_revenue: parseFloat(kw.total_revenue || kw.revenue || 0),
        total_clicks: parseInt(kw.total_clicks || kw.clicks || 0),
        avg_rpc: parseFloat(kw.avg_rpc || kw.rpc || 0),
        days_active: parseInt(kw.days_active || 0),
      }));
    },
    enabled: Boolean(adId && startDate && endDate) && (options?.enabled !== false),
    ...options,
  });
};

/**
 * Hook: useAdKeywordDaily
 * Fetches daily breakdown for a keyword in an ad
 */
export const useAdKeywordDaily = (
  adId: string,
  keyword: string,
  startDate: string,
  endDate: string,
  options?: Omit<UseQueryOptions<any[]>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: rsocKeys.adKeywordDaily(adId, keyword, startDate, endDate),
    queryFn: async () => {
      const daily = await rsocService.getAdKeywordDaily(adId, keyword, startDate, endDate);
      return daily.map((d: any) => ({
        date: d.date,
        rpc: parseFloat(d.rpc || 0),
        revenue: parseFloat(d.revenue || 0),
        clicks: parseInt(d.clicks || 0),
      }));
    },
    enabled: Boolean(adId && keyword && startDate && endDate) && (options?.enabled !== false),
    ...options,
  });
};
